import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { TourCompleteDialogComponent } from '@shared/components/tour-guide/tour-complete-dialog/tour-complete-dialog.component';
import { TourIntroDialogComponent } from '@shared/components/tour-guide/tour-intro-dialog/tour-intro-dialog.component';
import { getTourIdSelector } from '@shared/utils/ids.util';
import { ShepherdService } from 'angular-shepherd';
import { BehaviorSubject, Subject } from 'rxjs';
import { concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import Step from 'shepherd.js/src/types/step';
import Tour from 'shepherd.js/src/types/tour';
import { ApiBaseService, Mapper } from '@core/services';
import { ApiConstant } from '@shared/constants';
import { TourGuide, TourGuideItemTranslation } from '@shared/model/tour-guide.model';
import { AsyncFeedbackService } from './async-feedback.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from './loading.service';
import { SectionStateStatus } from '@shared/enums';

export type TourType = Tour & TourGuide;

export type StepOptionsType = Step.StepOptions & {
  position: string;
  description: string;
  originalTitle: string;
  hasNextAction: boolean;
  isTextUpdated: boolean;
  translations: TourGuideItemTranslation[];
};

enum TourElementPositions {
  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',
}

@Injectable({ providedIn: 'root' })
export class TourService {
  readonly toursList$ = new BehaviorSubject<TourType[]>([]);
  readonly startTour$ = new Subject<TourType>();
  readonly tourStarted$ = new Subject<void>();

  private currentWorkingTour: TourType;
  private currentTour: TourType;
  private loadingState: SectionStateStatus;
  private introDialogRef: MatDialogRef<TourIntroDialogComponent>;

  constructor(
    private readonly shepherdService: ShepherdService,
    private readonly matDialog: MatDialog,
    private baseAPI: ApiBaseService,
    private mapper: Mapper,
    protected asyncFeedbackService: AsyncFeedbackService,
    protected translationService: TranslateService,
    protected loadingService: LoadingService
  ) {
    this.loadingService.loadingStatus$.subscribe((value) => (this.loadingState = value));

    this.startTour$
      .pipe(filter(() => !this.currentWorkingTour && (!this.introDialogRef || this.introDialogRef?.getState() === MatDialogState.CLOSED)))
      .subscribe((tour) => this.startTour(tour));
  }

  private startTour(tour: TourType): void {
    if (this.currentWorkingTour) return;

    this.currentTour = tour;
    this.introDialogRef = this.matDialog.open(TourIntroDialogComponent, {
      data: {
        title: tour.title,
        message: tour.description,
      },
      width: '540px',
    });

    this.introDialogRef.afterClosed()
      .pipe(
        take(1),
        concatMap(() => this.setIsVisited())
      ).subscribe();

    this.introDialogRef.componentInstance.takeTour.pipe(take(1)).subscribe(() => {
      this.introDialogRef.close();
      this.shepherdService.addSteps(tour.steps);
      // Hint: tourObject becomes available after adding steps
      this.currentWorkingTour = this.shepherdService.tourObject as TourType;
      this.addEventListeners();
      this.tourStarted$.next();
      this.shepherdService.start();
    });
  }

  // called only in app-component
  initTour(): void {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      cancelIcon: { enabled: false },
      scrollTo: false,
      modalOverlayOpeningPadding: 8,
      modalOverlayOpeningRadius: 4,
    };
  }

  private addEventListeners(): void {
    if (!this.currentWorkingTour) return;

    const onShow = (data: { step: Step }) => {
      const index =
        this.currentWorkingTour?.steps.findIndex(
          (step) => step.options.attachTo.element === data.step.options.attachTo.element
        ) || 0;
      const stepOptions = data.step.options as StepOptionsType;

      if (!stepOptions.isTextUpdated) {
        stepOptions.text = this.createStepUI(
          stepOptions.originalTitle,
          stepOptions.description,
          this.currentWorkingTour.steps.length,
          index
        );
        stepOptions.isTextUpdated = true;
      }
    };

    const onComplete = () => {
      this.currentWorkingTour = undefined;
      this.removeEventListeners(onShow, onComplete, onCancel);
      this.openCompleteDialog();
    };

    const onCancel = () => {
      const currentStep = this.currentWorkingTour.getCurrentStep();
      const lastStep = {
        ...this.currentWorkingTour.steps[this.currentWorkingTour.steps.length - 1],
      };

      this.currentWorkingTour = undefined;
      this.removeEventListeners(onShow, onComplete, onCancel);
      if (currentStep.id === lastStep.id) {
        this.openCompleteDialog();
      } else {
        this.currentTour = undefined;
      }
    };

    this.currentWorkingTour?.on('show', onShow);
    this.currentWorkingTour?.on('complete', onComplete);
    this.currentWorkingTour?.on('cancel', onCancel);
  }

  private removeEventListeners(
    onShow: (data: { step: Step }) => void,
    onComplete: () => void,
    onCancel: () => void
  ): void {
    if (!this.currentWorkingTour) return;
    this.currentWorkingTour?.off('show', onShow);
    this.currentWorkingTour?.off('complete', onComplete);
    this.currentWorkingTour?.off('cancel', onCancel);
  }

  private openCompleteDialog(): void {
    this.currentTour = undefined;
    const dialogRef = this.matDialog.open(TourCompleteDialogComponent, {
      data: this.toursList$.value,
      width: '440px',
    });

    dialogRef.componentInstance.takeTour.pipe(take(1)).subscribe((tour) => {
      dialogRef.close();
      this.startTour(tour);
    });
  }

  private mapStepButtons(step: StepOptionsType, index: number, steps: StepOptionsType[]) {
    const isLastStep = index === steps.length - 1;
    const stepButtons = [
      {
        classes: 'shepherd-button-secondary mr-auto bg-white text-primary-200',
        text: this.translationService.instant('shared.Buttons.Close'),
        type: 'cancel',
      },
      ...((step?.hasNextAction && !isLastStep
        ? [
            {
              classes: 'bg-primary text-white px-4',
              text: this.translationService.instant('shared.Buttons.Next'),
              type: 'next',
            },
          ]
        : []) as any),
    ];

    if (isLastStep) {
      stepButtons[stepButtons.length - 1] = {
        ...stepButtons[stepButtons.length - 1],
        text: this.translationService.instant('shared.Buttons.GotIt'),
        action: () => this.currentWorkingTour.complete(),
      };
    }
    return stepButtons;
  }

  private createStepUI(title: string, text: string, totalSteps: number, stepIndex: number) {
    return `
        <div>
          <div class="flex align-center mb-4">
            ${Array.from(
              { length: totalSteps },
              (_, i) => `
              <div style="height: 2px" class="radius-1 m-0 mr-2 px-3 ${
                i <= stepIndex ? 'bg-accent' : 'bg-200'
              }"></div>
            `
            ).join('')}
            <div class="ml-auto text-400 small">${stepIndex + 1} / ${totalSteps}</div>
          </div>

         <div class="h6 text-900 weight-semibold">${title}</div>
          <div class="mb-4">${text}</div>
          <div class="brd brd-1 radius-1 text-200 mb-4"></div>
        </div>
      `;
  }

  private mapStepsContent(steps: StepOptionsType[]) {
    return steps.map((step, index) => {
      return {
        ...step,
        isTextUpdated: false, // prevent content duplication and update only one time inside the onShow()
        originalTitle: step?.translations[0]?.title || '',
        description: step?.translations[0]?.description || '',
        title: '', // Remove title to remove it's element form shephered, to customize UI with createStepUI()
        classes: 'p-4',
        buttons: this.mapStepButtons(step, index, steps),
        beforeShowPromise: () => {
          return new Promise((resolve, reject) => {
            const selector = step.attachTo.element.toString();
            const maxWaitMs = 5000;
            const intervalMs = 250;
            let waited = 0;

            const checkElementExist = setInterval(() => {
              const el = document.querySelector(selector);
              const isLoading = [
                SectionStateStatus.Loading,
                SectionStateStatus.LoadingTransparent,
              ].includes(this.loadingState);

              if (el) {
                clearInterval(checkElementExist);
                const scrollDelay = selector.includes('sidebar') ? 0 : 600;
                setTimeout(() => {
                  el.scrollIntoView({
                    behavior: 'smooth',
                    block: selector.includes('sidebar') ? 'center' : 'nearest',
                    inline: 'start',
                  })
                }, scrollDelay);
                resolve(true);
              } else {
                waited += intervalMs;
                if (waited >= maxWaitMs || (waited >= 3000 && !isLoading)) {
                  clearInterval(checkElementExist);
                  this.shepherdService.cancel();
                  this.currentTour = undefined;
                  this.asyncFeedbackService.showFailureMessage(
                    this.translationService.instant('shared.ElementNotAvailableInUI')
                  );
                  console.error('Element not found: ', selector);
                  reject('Element not found: ' + selector);
                }
              }
            }, intervalMs);
          });
        },
      };
    });
  }

  private mapStepsSelector(steps: StepOptionsType[]): StepOptionsType[] {
    return steps.map((step) => {
      const elementSelector = getTourIdSelector(step.position);

      return {
        ...step,
        attachTo: { element: elementSelector, on: TourElementPositions.TopStart },
        ...(step.hasNextAction ? {} : { advanceOn: { selector: elementSelector, event: 'click' } }),
      };
    });
  }

  getToursList() {
    return this.baseAPI.get(ApiConstant.GET_TOUR_GUIDES).pipe(
      map((res) => this.mapper.fromJson(TourGuide, res.data)),
      map((toursList) => toursList.sort((a, b) => b.id - a.id)),

      map((tourList) => {
        return tourList.map((tour) => ({
          ...tour,
          steps: tour?.steps.sort((a, b) => a.id - b.id),
        }));
      }),
      map((toursList) =>
        toursList.map((tour) => ({
          ...tour,
          steps: this.mapStepsSelector(tour.steps as unknown as StepOptionsType[]),
        }))
      ),
      map((toursList) =>
        toursList.map((tour) => ({ ...tour, steps: this.mapStepsContent(tour.steps) }))
      ),
      tap((toursList) => this.toursList$.next(toursList as unknown as TourType[]))
    );
  }

  setIsVisited() {
    return this.baseAPI
      .put(ApiConstant.TOUR_GUIDE_VISIT, { tourGuideId: this.currentTour.id, isVisited: true })
      .pipe(switchMap(() => this.getToursList()));
  }

  removeSideBarSteps(steps: StepOptionsType[]): StepOptionsType[] {
    return steps.slice(
      steps.findIndex((step) => !step.attachTo.element.toString().includes('sidebar'))
    );
  }
}
